import {AxiosError, AxiosResponse} from 'axios'
import {useFormContext} from 'react-hook-form'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {TreatmentRequestExtended} from '@/features/treatment-requests/types'
import {remapTreatmentRequestToDefaultValues} from '@/features/treatment-requests/utils'
import {useTreatmentRequestDetailsStore} from '@/features/treatment-requests/store/treatmentRequestDetailsStore'
import {FormErrorResponse} from '@/types'
import {useScrollContext} from '@/hooks/useScrollContext'
import {z, ZodSchema} from 'zod'

export const useTreatmentRequestFormSubmission = ({formSchema}: {formSchema: ZodSchema}) => {
    const {t} = useTranslation()
    const form = useFormContext<z.infer<typeof formSchema>>()
    const setEditingFieldsGroup = useTreatmentRequestDetailsStore(state => state.setEditingFieldsGroup)
    const isErrorBannerVisible = useTreatmentRequestDetailsStore(state => state.isErrorBannerVisible)
    const setIsErrorBannerVisible = useTreatmentRequestDetailsStore(state => state.setIsErrorBannerVisible)
    const {scrollToTop} = useScrollContext()

    const onSuccessSubmit = (response: AxiosResponse<TreatmentRequestExtended>) => {
        toast.success(t('commons:update_success'))
        setEditingFieldsGroup(null)
        if (isErrorBannerVisible) {
            setIsErrorBannerVisible(false)
        }

        const remappedResponse = remapTreatmentRequestToDefaultValues(response.data)
        form.reset(remappedResponse)

        // Update the query cache
        queryClient.setQueryData([QUERY_KEYS.TREATMENT_REQUESTS, response.data.id], response.data)
        void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.TREATMENT_REQUESTS]})
        // The patient snapshot embeds the request (type, sector, authorization, status) and feeds the
        // project creation flow, so it must not keep serving the pre-update request for the whole staleTime.
        void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS, response.data.patientId]})
    }

    const onErrorSubmit = (errors: AxiosError<{errors: FormErrorResponse[]}>) => {
        console.error(errors)
        setIsErrorBannerVisible(true)

        if (errors?.response?.data.errors.some(error => error.rule === 'checkSector')) {
            toast.error(t('treatment_requests:check_sector_error'))
        }

        scrollToTop()
    }

    return {
        onSuccessSubmit,
        onErrorSubmit,
        scrollToTop
    }
}
